import { useCallback, useEffect, useMemo, useRef } from "react";

import entries from "lodash/entries";
import { parse } from "date-fns";

import { isValidFilter } from "~/pages/books/components/filters/utils";
import { useSearchParams } from "~/hooks/use-search-params";
import {
  AgeRangeFilter,
  FilterType,
  SalesDataFilter,
  SearchQuery,
  SearchQueryFilters,
  SearchType,
} from "./types";
import { paramsToSearchQuery } from "./utils";
import { DATE_FORMAT, formatUrlDate } from "./constants";
import { useFilters } from "./use-filters";
import {
  formatSalesDataMetricFilterOperator,
  getActiveSalesDataMetrics,
  isActiveSalesDataFilter,
} from "./sales-data-filter-utils";

function appendParsedDateRangeFilter(
  params: URLSearchParams,
  field: string,
  from: string,
  to: string,
): void {
  const fromDate = parse(from, DATE_FORMAT, new Date());
  const toDate = parse(to, DATE_FORMAT, new Date());
  if (fromDate > toDate) return;
  if (from === to) {
    params.append("filter", `${field}.eq.${formatUrlDate(from)}`);
    return;
  }
  params.append("filter", `${field}.gte.${formatUrlDate(from)}`);
  params.append("filter", `${field}.lte.${formatUrlDate(to)}`);
}

function appendAgeRangeFilter(
  params: URLSearchParams,
  field: string,
  filter: AgeRangeFilter,
): void {
  const { ageType, range } = filter;
  params.append("filter", `${field}.eq.${ageType}`);
  params.append("filter", `${field}.gte.${range.min}`);
  params.append("filter", `${field}.lte.${range.max}`);
}

function appendDateRangeFilter(
  params: URLSearchParams,
  field: string,
  range: { from?: string; to?: string } | undefined,
): void {
  const { from, to } = range ?? {};
  if (!from || !to) return;
  appendParsedDateRangeFilter(params, field, from, to);
}

function appendSalesDataFilter(
  params: URLSearchParams,
  field: string,
  filter: SalesDataFilter,
  salesDataMaxDate: Date,
): void {
  if (!isActiveSalesDataFilter(filter, salesDataMaxDate)) return;

  const { from, to } = filter.range;
  if (from && to) {
    appendParsedDateRangeFilter(params, field, from, to);
  }

  getActiveSalesDataMetrics(filter).forEach((metric) => {
    params.append(
      "filter",
      `${field}.${formatSalesDataMetricFilterOperator(
        metric.key,
        metric.comparisonOperator,
      )}.${metric.value}`,
    );
  });
}

export function queryToParams(query: Partial<SearchQuery>): string {
  const params = new URLSearchParams();
  const salesDataMaxDate = new Date();
  if (query.query) params.set("q", query.query);
  if (query.type && query.type !== SearchType.QUICK) {
    params.set("type", query.type);
  }
  if (query.type !== SearchType.SEMANTIC) {
    if (query.page) params.set("page", String(query.page));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));
  }

  entries(query.filters ?? {}).forEach(([field, f]) => {
    if (!f || !isValidFilter(f, salesDataMaxDate)) return;
    switch (f.type) {
      case FilterType.NUMERIC:
        if (typeof f.value === "number" && f.comparisonOperator) {
          params.append(
            "filter",
            `${field}.${f.comparisonOperator}.${f.value}`,
          );
        }
        break;
      case FilterType.DATE_RANGE:
        appendDateRangeFilter(params, field, f.range);
        break;
      case FilterType.AGE_RANGE:
        appendAgeRangeFilter(params, field, f);
        break;
      case FilterType.CHECKBOX:
        f.values?.forEach((v) => {
          params.append("filter", `${field}.in.${v}`);
        });
        break;
      case FilterType.SALES_DATA:
        appendSalesDataFilter(params, field, f, salesDataMaxDate);
        break;
      default:
        break;
    }
  });

  return params.toString();
}

export function useSearchQuery() {
  const [params, setSearchParams] = useSearchParams();
  const { filters } = useFilters();

  const query = useMemo(
    () => paramsToSearchQuery(params, filters),
    [params, filters],
  );

  const queryRef = useRef(query);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const setQuery = useCallback(
    (updated: Partial<SearchQuery>) => {
      const value = { ...queryRef.current, ...updated };

      setSearchParams(new URLSearchParams(queryToParams(value)), {
        replace: true,
      });
    },
    [setSearchParams],
  );

  const removeQueryParameter = useCallback(
    (key: keyof SearchQueryFilters) => {
      const updatedFilters = { ...query.filters };
      delete updatedFilters[key];
      setQuery({ filters: updatedFilters });
    },
    [setQuery, query.filters],
  );

  return { query, setQuery, removeQueryParameter };
}
